import { useEffect, useState } from 'react';

export const useScrollIndicatorProgress = (elementRef: React.RefObject<HTMLElement>) => {
  const [intersectionRatio, setIntersectionRatio] = useState(0);

  useEffect(() => {
    const viewPortHeight = window.innerHeight;
    const tenPercentViewportHeight = viewPortHeight / 10;

    const handleIntersections = (entries: IntersectionObserverEntry[]) => {
      const [entry] = entries;

      // If the lower end of the IndicatorBar (including 10vh margin)
      // is in viewport, we assume the IndicatorBar is fully visible and return a ratio of 1.0
      if (entry.boundingClientRect.bottom + tenPercentViewportHeight <= viewPortHeight) {
        setIntersectionRatio(1);
        return;
      }

      setIntersectionRatio(entry.intersectionRatio);
    };

    // The threshold defines when the call back (handleIntersections) will fire: For the first time,
    // when the intersection reaches 0.1, then again on 0.2 and so on. The (negative) rootMargin makes the
    // viewPort 20vh shorter, so that the intersectionRatio will reach 1.0 after the whole indicatorBar has
    // scrolled into view already. That way there's always a small gap in the percentage filled in the indicatorBar
    // until the next element, the one the user should be scrolling into view, is already partially visible.
    // Also the indicatorBar will start filling a tad after /really/ intersecting with the viewport.

    const observer = new IntersectionObserver(handleIntersections, {
      threshold: [0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1],
      rootMargin: `-${tenPercentViewportHeight}px 0px `
    });
    const el = elementRef.current;

    if (el) {
      observer.observe(el);
    }

    return () => {
      if (el) {
        observer.unobserve(el);
      }
    };
  }, [elementRef]);

  const progressPercentage = Math.round(intersectionRatio * 100);
  return progressPercentage >= 0 ? progressPercentage : 0;
};
